<!-- src/views/admin/AdminTrainingDashboardView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & COMMAND FILTERS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative z-30 overflow-visible border-x border-t border-slate-800">
          <div class="absolute inset-0 rounded-[40px] overflow-hidden pointer-events-none">
            <div class="absolute top-0 right-0 p-12 opacity-5">
              <AcademicCapIcon class="h-32 w-32 text-white" />
            </div>
          </div>
          
          <div class="relative z-10 text-left">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Training Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Competency Registry • Briefing Oversight & Authorisation</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <router-link v-slot="{ href, navigate }" v-if="hasTrainingClearance" to="/admin/training/toolbox-talks" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 no-underline cursor-pointer">
                    <ChatBubbleLeftRightIcon class="h-4 w-4" /> <span>Manage Talks</span>
                  </a>
                </router-link>

                <router-link v-slot="{ href, navigate }" v-if="hasTrainingClearance" to="/admin/training-options" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg no-underline cursor-pointer">
                    <AdjustmentsHorizontalIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Manage Options</span>
                  </a>
                </router-link>

                <!-- 🛡️ NAVIGATION STANDARDISED: Updated button text to 'Return' and matched padding -->
                <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline cursor-pointer">
                    <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                  </a>
                </router-link>
              </div>
            </div>

            <!-- FILTER ENGINE -->
            <div class="bg-white/5 p-5 rounded-[32px] border border-white/10 relative">
              <div class="flex flex-wrap items-end gap-3">
                <div class="flex flex-col gap-1.5 flex-grow min-w-[180px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                  <input v-model="filters.search" type="text" placeholder="Personnel or Topic..." class="filter-input-dark h-[46px]" />
                </div>

                <div class="flex flex-col gap-1.5 w-full sm:w-[220px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Registry Selection</span>
                  <select v-model="activeMode" class="filter-select-dark h-[46px]">
                    <option value="talks">Toolbox Briefings ({{ tabCounts.talks || 0 }})</option>
                    <option value="requests">Training Requests ({{ tabCounts.requests || 0 }})</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 w-full sm:w-[130px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Period</span>
                  <select v-model="filters.period" class="filter-select-dark h-[46px]">
                    <option value="all">All Time</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="3months">3 Months</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 w-full sm:w-[130px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Status</span>
                  <select v-model="filters.status" class="filter-select-dark h-[46px]">
                    <option value="all">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                    <option value="Needs Attention">Feedback</option>
                  </select>
                </div>

                <!-- 🛡️ RE-STYLED EXPORT BUTTON: Styled exactly like the dark-glass Engineering export button -->
                <button 
                  @click="exportToExcel" 
                  :disabled="exporting || filteredData.length === 0" 
                  class="px-6 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 h-[46px] disabled:opacity-50 shadow-lg cursor-pointer border-none"
                >
                  <template v-if="exporting">
                    <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                    <span>Syncing...</span>
                  </template>
                  <template v-else>
                    <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                  </template>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS CARD -->
        <div class="mb-12 animate-fadeIn relative z-10 text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2">Active Competency Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg cursor-pointer border-none"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left animate-fadeIn">
                 <div v-for="stat in trainingStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current competency registrations, sign-off compliance, and attendee feedback alerts.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Competency Hotspots</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Complete Training Profiles
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Training Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 3. DATA REGISTRY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hydrating Training Registry...</p>
        </div>

        <div v-else-if="paginatedData.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner animate-fadeIn">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No matching training records identified.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn relative z-0">
          <div v-for="item in paginatedData" :key="item.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group text-left">
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
              
              <!-- QR & REFERENCE -->
              <div class="flex items-center gap-6 w-full xl:w-[28%] shrink-0">
                <div class="relative h-20 w-20 shrink-0 bg-white p-1 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                  <img v-if="qrCodeUrls[item.id]" :src="qrCodeUrls[item.id]" alt="QR" class="w-full h-full object-contain" />
                  <div v-else class="animate-pulse bg-slate-50 w-full h-full rounded-lg"></div>
                </div>
                <div class="min-w-0">
                  <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none mb-2">
                    {{ item.referenceNumber || item.humanReadableId || 'BT-' + item.id.substring(0,5).toUpperCase() }}
                  </h3>
                  <div class="flex flex-wrap gap-2">
                    <div v-if="item.type" class="inline-flex px-2 py-0.5 bg-slate-100 rounded-md text-[8px] font-black text-slate-500 uppercase tracking-widest">
                        {{ item.type }}
                    </div>
                    <div v-if="hasTechnicalFeedback(item)" class="inline-flex px-2 py-0.5 bg-rose-500 text-white rounded-md text-[8px] font-black uppercase tracking-widest animate-pulse items-center gap-1">
                        <ExclamationCircleIcon class="h-2.5 w-2.5" /> Feedback Received
                    </div>
                  </div>
                </div>
              </div>

              <!-- TOPIC CONTEXT -->
              <div class="flex-grow px-4 border-l border-slate-50 min-w-0 space-y-2">
                <h4 class="text-sm font-black text-[#0A1630] uppercase leading-none truncate group-hover:text-[#FD5E00] transition-colors">
                   {{ item.resolvedName || item.topic || 'Registry Entry' }}
                </h4>
                <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                   <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      {{ formatDate(item.submittedAt || item.date) }}
                   </span>
                   
                   <!-- 🛡️ DYNAMIC LOCATION RESOLVER (Issue #2): Renders 'locationOther' details if 'Other' was selected -->
                   <span v-if="item.location" class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">
                     • {{ item.location === 'Other' ? (item.locationOther || 'Other') : item.location }}
                   </span>
                   
                   <!-- 🛡️ PRESENTER IDENTITY (Raised By) -->
                   <span v-if="activeMode === 'talks' && item.talker" class="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                      <UserIcon class="h-3 w-3" /> Raised By: {{ item.talker.name }}
                   </span>
                </div>
              </div>

              <!-- STATUS & ACTIONS -->
              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                 <div class="text-right shrink-0">
                    <template v-if="activeMode === 'talks'">
                       <span 
                         :class="getTalkAttendeesStats(item).outstanding > 0 ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-[#EBFDF8] text-[#0A1630] border border-[#20FCF1]/30'"
                         class="px-5 py-2 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm border"
                       >
                         {{ getTalkAttendeesStats(item).outstanding }} / {{ getTalkAttendeesStats(item).total }} Outstanding
                       </span>
                    </template>
                    <template v-else>
                       <span :class="statusClass(item.status, item)" class="px-5 py-2 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">
                         {{ getDisplayStatus(item) }}
                       </span>
                    </template>
                 </div>

                 <router-link 
                  v-slot="{ href, navigate }"
                  v-if="activeMode === 'talks'"
                  :to="{ name: 'AdminToolboxTalkDetail', params: { talkId: item.id } }" 
                  custom
                >
                  <a :href="href" @click="navigate" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg min-w-[180px] text-center no-underline cursor-pointer">
                     <span>Review Briefing</span>
                  </a>
                </router-link>

                <router-link 
                  v-slot="{ href, navigate }"
                  v-else
                  :to="{ name: 'completedForm', params: { collectionName: 'onboardingFormSubmissions', submissionId: item.id }}" 
                  custom
                >
                  <a :href="href" @click="navigate" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg min-w-[180px] text-center no-underline cursor-pointer">
                     <span>Authorise Request</span>
                  </a>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
        <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
          <div class="flex items-center gap-4">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              &larr; Prev
            </button>
            <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
              Page {{ currentPage }} of {{ totalPages || 1 }}
            </span>
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              Next &rarr;
            </button>
          </div>
          
          <!-- Symmetrical Items Per Page Selector (Forced h-11 height) -->
          <div class="flex items-center gap-3">
            <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
            <select 
              v-model="itemsPerPage" 
              class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
              style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
            >
              <option :value="25">25 Rows</option>
              <option :value="50">50 Rows</option>
              <option :value="100">100 Rows</option>
            </select>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watchEffect, watch } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, doc, getDoc, orderBy, limit, onSnapshot, setDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import { 
  AcademicCapIcon, ArrowUturnLeftIcon,
  AdjustmentsHorizontalIcon, TableCellsIcon, MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon, ChartBarIcon, UserIcon,
  ExclamationCircleIcon, ExclamationTriangleIcon, SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const exporting = ref(false);

/** 🛡️ PRIORITY 1: Sticky Registry Selection & Search states [1.1.4] */
const activeMode = ref(localStorage.getItem('training_sticky_mode') || 'talks');

const tabCounts = reactive({ requests: 0, talks: 0 });

const requests = ref([]);
const talks = ref([]);
const qrCodeUrls = ref({});

// PAGINATION STATE (Defaulting to 25 rows with h-11 sizing logic)
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

const filters = reactive({
  search: localStorage.getItem('training_sticky_search') || '',
  status: localStorage.getItem('training_sticky_status') || 'all',
  period: localStorage.getItem('training_sticky_period') || 'month'
});

// 🛡️ STICKY WATCHERS: Write filter selections to localStorage dynamically [1.1.4]
watch(activeMode, (val) => localStorage.setItem('training_sticky_mode', val));
watch(() => filters.search, (val) => localStorage.setItem('training_sticky_search', val));
watch(() => filters.status, (val) => localStorage.setItem('training_sticky_status', val));
watch(() => filters.period, (val) => localStorage.setItem('training_sticky_period', val));

// Reset page back to 1 on tab/filter updates
watch([activeMode, () => filters.search, () => filters.status, () => filters.period, itemsPerPage], () => {
  currentPage.value = 1;
});

const hasTechnicalFeedback = (item) => {
    return item.attendees?.some(a => a.status === 'Needs Attention');
};

/** 🛡️ SIGNATURE COUNTER PROTOCOL [1.1.4] */
const getTalkAttendeesStats = (item) => {
  const total = item.attendees?.length || 0;
  const signed = item.attendees?.filter(a => a.status === 'Completed').length || 0;
  const outstanding = total - signed;
  return { total, signed, outstanding };
};

const trainingStats = computed(() => {
  const filtered = filteredData.value;
  let totalConfirmed = 0, totalAssigned = 0;
  filtered.forEach(t => {
      if (t.attendees) {
          totalAssigned += t.attendees.length;
          totalConfirmed += t.attendees.filter(a => a.status === 'Completed').length;
      }
  });
  const rate = totalAssigned > 0 ? Math.round((totalConfirmed / totalAssigned) * 100) : 0;

  return [
    { label: 'Registry Total', value: filtered.length, color: '#20FCF1', icon: AcademicCapIcon },
    { label: 'Awaiting Action', value: filtered.filter(f => f.status === 'Pending').length, color: '#FBBF24', icon: ChatBubbleLeftRightIcon },
    { label: 'Technical Feedback', value: filtered.filter(f => hasTechnicalFeedback(f)).length, color: '#EF4444', icon: ExclamationTriangleIcon },
    { label: 'Participation Rate', value: `${rate}%`, color: '#6366F1', icon: ChartBarIcon },
  ];
});

// Helper to extract numerical component from reference string for precise ordering
const getReferenceNumberNumeric = (refNum) => {
  if (!refNum) return 0;
  const match = refNum.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

const filteredData = computed(() => {
  const source = activeMode.value === 'requests' ? requests.value : talks.value;
  const now = new Date();

  const list = source.filter(item => {
    const name = (item.resolvedName || item.topic || '').toLowerCase();
    const refID = (item.referenceNumber || item.id || '').toLowerCase();
    const matchSearch = !filters.search || name.includes(filters.search.toLowerCase()) || refID.includes(filters.search.toLowerCase());

    let matchStatus = filters.status === 'all' || (item.status || '').toLowerCase() === filters.status.toLowerCase();
    if (filters.status === 'Needs Attention') matchStatus = hasTechnicalFeedback(item);

    const itemDate = (item.submittedAt || item.date)?.toDate ? (item.submittedAt || item.date).toDate() : new Date(item.submittedAt || item.date);
    let matchPeriod = true;
    if (filters.period !== 'all') {
        const diffDays = (now - itemDate) / (1000 * 60 * 60 * 24);
        if (filters.period === 'week' && diffDays > 7) matchPeriod = false;
        if (filters.period === 'month' && diffDays > 30) matchPeriod = false;
        if (filters.period === '3months' && diffDays > 90) matchPeriod = false;
    }
    return matchSearch && matchStatus && matchPeriod;
  });

  return list.sort((a, b) => {
    const refA = a.referenceNumber || a.humanReadableId || '';
    const refB = b.referenceNumber || b.humanReadableId || '';

    const numA = getReferenceNumberNumeric(refA);
    const numB = getReferenceNumberNumeric(refB);

    if (numA === numB) {
      const dateA = (a.submittedAt || a.date)?.toDate ? (a.submittedAt || a.date).toDate() : new Date(a.submittedAt || a.date);
      const dateB = (b.submittedAt || b.date)?.toDate ? (b.submittedAt || b.date).toDate() : new Date(b.submittedAt || b.date);
      return dateB - dateA;
    }
    return numB - numA;
  });
});

// PAGINATION MATH
const totalPages = computed(() => {
  return Math.ceil(filteredData.value.length / itemsPerPage.value);
});

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredData.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

const getDisplayStatus = (item) => {
    if (hasTechnicalFeedback(item)) return 'FEEDBACK';
    return item.status || 'ACTIVE';
};

const statusClass = (status, item) => {
  if (hasTechnicalFeedback(item)) return 'bg-rose-50 text-rose-600 border border-rose-100';
  const s = (status || 'PENDING').toUpperCase();
  if (s === 'COMPLETED') return 'bg-[#EBFDF8] text-[#0A1630] border border-[#20FCF1]/30';
  return 'bg-amber-50 text-[#0A1630] border border-amber-200';
};

// 🛡️ UNCONDITIONAL PLATFORM AUTHORITY OVERRIDE: Simplifies permissions so all Command Access Admins can manage everything
const hasTrainingClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewTraining === true ||
         userStore.permissions?.canManageTraining === true;
});

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const totalTalks = talks.value.length;
        const totalRequests = requests.value.length;
        const pendingRequests = requests.value.filter(r => r.status === 'Pending').length;
        const feedbackCount = talks.value.filter(t => hasTechnicalFeedback(t)).length;

        // Dynamic attendee sign-off calculations
        let totalInvited = 0;
        let totalSigned = 0;
        talks.value.forEach(t => {
            if (t.attendees) {
                totalInvited += t.attendees.length;
                totalSigned += t.attendees.filter(a => a.status === 'Completed').length;
            }
        });

        const participationRate = totalInvited > 0 ? Math.round((totalSigned / totalInvited) * 100) : 100;

        const report = {
            summary: `Competency and briefing audit analyzes ${totalTalks} toolbox talks and ${totalRequests} active training requests. Operational participation stands at a ${participationRate}% sign-off rate across all field attendees. Identified ${feedbackCount} toolbox talks with active technical feedback requiring review, and ${pendingRequests} training requests awaiting HR authorisation.`,
            trends: [
                { label: 'Participation Index', value: `${participationRate}% Sign-off rate`, isCritical: participationRate < 80 },
                { label: 'Technical Exceptions', value: `${feedbackCount} Feedback logs noted`, isCritical: feedbackCount > 0 },
                { label: 'Authorisation Slack', value: `${pendingRequests} Training requests pending`, isCritical: pendingRequests > 3 }
            ],
            actions: [
                { type: 'Technical Triage', instruction: `Address ${feedbackCount} briefings with feedback`, reason: 'Toolbox talks with attendee feedback flags indicate critical instructional gaps.' },
                { type: 'Approval Cycle', instruction: `Authorise ${pendingRequests} training requests`, reason: 'Outstanding employee competency requests stall workforce upskilling and certification logs.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'training_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'training_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Training Intelligence Registry Neutral.");
  }
};

const setupListeners = async (clientId) => {
  loading.value = true;
  await fetchLatestAiReport(clientId);

  const qRequests = query(collection(db, 'onboardingFormSubmissions'), where('clientId', '==', clientId), where('formIdentifier', '==', 'trainingRequest'), orderBy('submittedAt', 'desc'), limit(100));
  onSnapshot(qRequests, async (snap) => {
    const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    requests.value = await Promise.all(raw.map(async (docData) => {
        let resolvedName = docData.userName || 'Personnel';
        if (docData.userId) {
          try {
            const pSnap = await getDoc(doc(db, 'userProfiles', docData.userId));
            if (pSnap.exists()) resolvedName = `${pSnap.data().firstName} ${pSnap.data().lastName}`;
          } catch (e) { /* Silent skip */ }
        }
        const url = `${window.location.origin}/completed-form/onboardingFormSubmissions/${docData.id}`;
        if (!qrCodeUrls.value[docData.id]) { try { qrCodeUrls.value[docData.id] = await QRCode.toDataURL(url, { width: 100, margin: 1 }); } catch (e) { console.error(e); } }
        return { ...docData, resolvedName };
    }));
    tabCounts.requests = requests.value.length;
    loading.value = false;
  });

  const qTalks = query(collection(db, 'toolboxTalks'), where('clientId', '==', clientId), orderBy('date', 'desc'), limit(100));
  onSnapshot(qTalks, async (snap) => {
    const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    talks.value = await Promise.all(raw.map(async (talk) => {
        const url = `${window.location.origin}/admin/training/toolbox-talks/${talk.id}`;
        if (!qrCodeUrls.value[talk.id]) { try { qrCodeUrls.value[talk.id] = await QRCode.toDataURL(url, { width: 100, margin: 1 }); } catch (e) { console.error(e); } }
        return talk;
    }));
    tabCounts.talks = talks.value.length;
  });
};

const formatDate = (val) => val ? (val.toDate ? val.toDate() : new Date(val)).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A';

/** 🛡️ PRIORITY 3: High-Fidelity Comprehensive Excel Export */
const exportToExcel = () => {
  exporting.value = true;
  try {
    const dataToExport = filteredData.value.map(item => {
      const common = {
        'Reference': item.referenceNumber || item.humanReadableId || item.id,
        'Classification': item.type || 'N/A',
        'Personnel/Topic': item.resolvedName || item.topic,
        'Status': getDisplayStatus(item),
        'Date/Time': formatDate(item.submittedAt || item.date),
        'Location': item.location === 'Other' ? (item.locationOther || 'Other') : (item.location || 'N/A'),
      };

      if (activeMode.value === 'talks') {
        const total = item.attendees?.length || 0;
        const signed = item.attendees?.filter(a => a.status === 'Completed').length || 0;
        const pending = total - signed;
        const rate = total > 0 ? Math.round((signed / total) * 100) : 0;

        // 🛡️ DYNAMIC AUDIT CHASE TRAIL: Compiles comma-separated list of personnel who have not signed
        const pendingNames = item.attendees
          ?.filter(a => a.status !== 'Completed')
          ?.map(a => a.name)
          ?.join(', ') || 'None';

        return {
          ...common,
          'Raised By': item.talker?.name || 'N/A',
          'Details Summary': item.details?.replace(/<[^>]*>/g, '') || 'N/A',
          'Total Invited': total,
          'Total Signed': signed,
          'Total Pending': pending,
          'Pending Sign-offs': pendingNames,
          'Completion %': `${rate}%`,
          'Critical Alert': item.isCritical ? 'Yes' : 'No'
        };
      } else {
        return {
          ...common,
          'Job Title': item.formData?.jobTitle || 'N/A',
          'Department': item.formData?.department || 'N/A',
          'Request Summary': item.formData?.comments || 'N/A'
        };
      }
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Training_Audit_Registry");
    XLSX.writeFile(wb, `Sentinel_Training_Audit_${new Date().toISOString().split('T')[0]}.xlsx`);
  } catch (err) { 
    alert("Export Failure: " + err.message); 
  } finally { 
    exporting.value = false; 
  }
};

watchEffect(() => {
  const cid = userStore.clientId;
  if (cid && userStore.isAuthReady) setupListeners(cid);
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-4 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 shadow-inner; }
.filter-select-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-4 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 1rem center; background-size: 1rem; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #20FCF1; border-radius: 10px; }
</style>